import { useCallback, useMemo } from "react"
import { useQuery } from "urql"

import { GetVendorPayableFieldsDocument, type GetVendorPayableFieldsQuery } from "@/graphql/codegen/graphql"

type VendorPayableCompany = Extract<
  GetVendorPayableFieldsQuery["company"]["get"],
  { __typename: "CompanyGetSuccess" }
>["company"]

export interface VendorPayableFields {
  sensitiveAccount: boolean | null
  defaultExpenseAccountId: string | null
  defaultExpenseAccount: NonNullable<VendorPayableCompany["defaultExpenseAccount"]> | null
}

const EMPTY_FIELDS: VendorPayableFields = {
  sensitiveAccount: null,
  defaultExpenseAccountId: null,
  defaultExpenseAccount: null,
}

export interface UseGetVendorPayableFieldsReturn {
  fields: VendorPayableFields
  isFetching: boolean
  isResolved: boolean
  refetch: () => void
}

export const useGetVendorPayableFields = (companyId: string, pause = false): UseGetVendorPayableFieldsReturn => {
  const [{ data, fetching }, reexecuteQuery] = useQuery({
    query: GetVendorPayableFieldsDocument,
    variables: { input: { companyId } },
    pause,
  })

  const refetch = useCallback(() => reexecuteQuery({ requestPolicy: "network-only" }), [reexecuteQuery])

  const fields = useMemo<VendorPayableFields>(() => {
    if (data?.company.get.__typename !== "CompanyGetSuccess") return EMPTY_FIELDS

    const { sensitiveAccount, defaultExpenseAccount } = data.company.get.company
    return {
      sensitiveAccount: sensitiveAccount ?? null,
      defaultExpenseAccountId: defaultExpenseAccount?.glAccountId ?? null,
      defaultExpenseAccount: defaultExpenseAccount ?? null,
    }
  }, [data])

  return {
    fields,
    isFetching: fetching,
    isResolved: data?.company.get.__typename === "CompanyGetSuccess",
    refetch,
  }
}
